import type { Translations } from '../types';

export const nl: Translations = {
    identityVerification: {
        title: 'Bevestig uw identiteit',
        mockNotice:
            'Testmodus. Evrotrust is niet aangesloten. Er worden geen identiteitsdocumenten of biometrische gegevens verzameld.',
        consent: 'Ik begrijp dat dit een test is en ga akkoord met het opslaan van een testresultaat.',
        continue: 'Doorgaan met verificatie',
        help: 'Neem voor hulp contact op met uw bedrijfsvertegenwoordiger.',
        expires: 'Uitnodiging geldig tot {date}',
        invalidTitle: 'Uitnodiging niet beschikbaar',
        invalidDescription:
            'De link is verlopen, ingetrokken of niet meer geldig. Neem contact op met uw bedrijfsvertegenwoordiger.',
        error: 'Het verzoek is mislukt. Probeer het opnieuw.',
        retry: 'Opnieuw proberen',
        loading: 'Uitnodiging laden…',
        successTitle: 'Verificatie voltooid',
        successDescription: 'Uw testresultaat is opgeslagen. U kunt deze pagina sluiten.',
        roles: {
            admin: 'Directeur',
            beneficiary: 'Begunstigde',
            beneficiary_admin: 'Directeur en begunstigde',
        },
    },
    kyc: {
        title: 'Documentcontrole',
        openSection: 'Documentcontrole openen',
        progress: 'Controlestatus',
        progressCount: '{completed} van {total} verplichte stappen voltooid',
        refresh: 'Vernieuwen',
        hint: 'Upload de ontbrekende documenten. De statussen worden bijgewerkt op basis van de controle.',
        loading: 'Controle laden…',
        retry: 'Opnieuw proberen',
        empty: 'Er zijn momenteel geen stappen beschikbaar. Probeer het later opnieuw of neem contact op met ondersteuning.',
        documents: 'Bedrijfsdocumenten',
        identity: 'Identiteitscontrole',
        otherChecks: 'Aanvullende controles',
        rejectedHint: 'De controle is niet geslaagd. Neem zo nodig contact op met ondersteuning.',
        openDocument: 'Document openen',
        upload: 'Bestand uploaden',
        uploadAgain: 'Opnieuw uploaden',
        uploading: 'Uploaden…',
        uploadSaved: 'Het document is opgeslagen en ter controle ingediend.',
        continue: 'Doorgaan',
        successTitle: 'Bedrijfscontrole voltooid',
        successDescription: 'Alle verplichte controlestappen zijn voltooid.',
        done: 'Gereed',
        statuses: {
            completed: 'Goedgekeurd',
            failed: 'Controle niet geslaagd',
            in_progress_auto: 'Automatische controle',
            in_progress_manual: 'In beoordeling',
            upload: 'Upload vereist',
            waiting: 'Wacht op controle',
            notRequired: 'Niet vereist',
        },
        errors: {
            emptyFile: 'Het geselecteerde bestand is leeg.',
            largeFile: 'Het bestand mag niet groter zijn dan 10 MiB.',
            invalidFile: 'Kies een PDF of een JPEG-, PNG-, GIF-, BMP- of WebP-afbeelding.',
            conflict: 'De stap is gewijzigd. De gegevens worden vernieuwd; controleer de beschikbare acties.',
            upload: 'De upload kon niet worden bevestigd. Controleer de vernieuwde lijst voordat u opnieuw uploadt.',
            load: 'De controlegegevens konden niet worden vernieuwd. Probeer het opnieuw.',
        },
        stages: {
            kbo: {
                title: 'KBO-uittreksel',
                description: 'Bedrijfsgegevens uit het officiële register.',
            },
            company_charter: {
                title: 'Statuten',
                description: 'De huidige statuten, inclusief wijzigingen.',
            },
            ubo: {
                title: 'UBO-register',
                description: 'Informatie over de uiteindelijke begunstigden van het bedrijf.',
            },
            annual_report: {
                title: 'Jaarverslag',
                description: 'Financiële verslaggeving over het laatst afgesloten jaar.',
            },
            admin_id: {
                title: 'Identiteit van bestuurders',
                description: 'Algemene controlestatus van de huidige bestuurders.',
            },
            beneficiaries: {
                title: 'Identiteit van begunstigden',
                description: 'Algemene controlestatus van de uiteindelijke begunstigden.',
            },
            pep_individual: {
                title: 'Aanvullende bestuurderscontrole',
                description: 'Deze controle wordt door ons team uitgevoerd.',
            },
            pep_entity: {
                title: 'Aanvullende bedrijfscontrole',
                description: 'Deze controle wordt door ons team uitgevoerd.',
            },
            risk_report: {
                title: 'Eindbeoordeling',
                description: 'Deze controle wordt door ons team uitgevoerd.',
            },
            bank_account: {
                title: 'Bankgegevens',
                description: 'Algemene controlestatus van de bankgegevens.',
            },
            other: {
                title: 'Aanvullende controle',
                description: 'Controlestatus van het bedrijf.',
            },
        },
    },
    common: {
        close: 'Sluiten',
        ok: 'OK',
    },
    sidebar: {
        appTitle: 'Office Hof',
        menu: {
            mail: 'Post',
            office: 'Kantoor',
            payment: 'Betaling',
            support: 'Support',
            profile: 'Profiel',
            logout: 'Uitloggen',
        },
    },
    login: {
        title: 'Log in op uw account',
        subtitle: 'Persoonlijk account',
        loadingData: 'Uw gegevens laden',
        form: {
            emailLabel: 'Login',
            emailPlaceholder: 'E-mailadres',
            passwordLabel: 'Wachtwoord',
            passwordPlaceholder: 'Voer wachtwoord in',
            rememberMe: 'Onthoud mij',
            rememberMeTooltip: 'Logingegevens 3 maanden bewaren',
            submitButton: 'Inloggen',
            submitButtonLoading: 'Bezig met inloggen...',
            forgotPassword: 'Wachtwoord vergeten?',
            noAccountText: 'Nog geen account?',
            registerLink: 'Registreren',
            forgotPasswordEmailSubject: 'Verzoek om wachtwoord te wijzigen',
            forgotPasswordEmailBody:
                'Verzoek om het wachtwoord te wijzigen voor mijn account:\nemail: {email},\nbedrijf: {company}.',
            forgotPasswordEmailRequiredTitle: 'Voer e-mailadres in',
            forgotPasswordEmailRequiredMessage:
                'Voer eerst het e-mailadres van uw account in het veld hierboven in en klik daarna op «Wachtwoord vergeten?».',
            selectCompany: 'Bedrijf',
            passwordDisabledHint: 'Voer een geldig e-mailadres in',
            recoveryTitle: 'Herstel',
            loginLabel: 'Inloggen',
            companyLabel: 'Bedrijf',
            newPasswordLabel: 'Uw nieuw wachtwoord',
            copyButton: 'Kopiëren',
            savePasswordWarning: 'Onthoud het nieuwe wachtwoord!',
            recoveryFailedMessage: 'Toegang herstellen mislukt, probeer het opnieuw',
            backToLogin: 'Terug naar inloggen',
            confirmRecoveryMessage: 'Bevestig het herstellen van het account',
            confirmRecoveryButton: 'Bevestigen',
        },
        validation: {
            emailRequired: 'Voer e-mailadres in',
            emailInvalid: 'Voer een geldig e-mailadres in',
            passwordRequired: 'Voer wachtwoord in',
            incorrectCredentials: 'Onjuiste login of wachtwoord',
            userNotFound: 'Gebruiker niet gevonden',
        },
        errors: {
            authError: 'Authenticatiefout',
            invalidCredentials: 'Ongeldige login of wachtwoord',
            tooManyAttempts: 'Te veel pogingen, probeer het later opnieuw',
            logoutError: 'Uitloggen mislukt',
        },
    },
    registration: {
        title: 'Registratie',
        emailLabel: 'E-mailadres',
        emailPlaceholder: 'Voer uw e-mail in',
        continueButton: 'Doorgaan',
        haveAccount: 'Heeft u al een account?',
        signInLink: 'Inloggen',
        confirmTitle: 'Bevestiging e-mailadres',
        confirmMessage: 'Voer de code in die we hebben verzonden naar {email}',
        confirmBack: 'Terug',
        changeEmail: 'Ander e-mailadres gebruiken',
        confirmSpamHint: 'Geen code? Controleer de map Spam.',
        confirmRequestAgainIn: 'U kunt over {time} een nieuwe code aanvragen.',
        confirmRequestAgainButton: 'Opnieuw aanvragen',
        validation: {
            emailRequired: 'Voer e-mail in',
            emailInvalid: 'Voer een geldig e-mailadres in',
        },
        errors: {
            invalidEmail: 'Ongeldig e-mailadres',
            rateLimit: 'Te veel pogingen. Probeer het later opnieuw.',
            serverError: 'Serverfout, probeer het later opnieuw.',
            wrongCode: 'Onjuiste code',
        },
        form: {
            stepSelectTariff: 'Kies een tarief',
            stepCompanyDetails: 'Vul de gegevens van uw bedrijf in',
            stepAddressDetails: 'Vul uw postadres in',
            addressForwardingHint: 'Papieren brieven en pakketten worden naar dit adres doorgestuurd',
            stepPersonalDetails: 'Vul uw persoonlijke gegevens in',
            stepComplete: 'Klaar!',
            next: 'Volgende',
            back: 'Terug',
            finish: 'Klaar',
            stepSelectTariffStub: 'Hier verschijnt de tariefkeuze.',
            stepCompanyDetailsStub: 'Hier verschijnen de velden voor uw bedrijf.',
            stepPersonalDetailsStub: 'Hier verschijnen uw persoonlijke gegevens.',
            stepCompleteStub: 'Registratie voltooid. Tik op Klaar om in te loggen.',
            loadingRegCustomerData: 'Gegevens laden',
            selectOfficeLabel: 'Kies een kantoor',
            selectTariffLabel: 'Kies een tarief',
            officeBrussels: 'Brussel',
            officeFlanders: 'Vlaanderen',
            companyNameLabel: 'Bedrijfsnaam',
            companyNamePlaceholder: 'Voer de bedrijfsnaam in',
            activityLabel: 'Activiteit',
            activityPlaceholder: 'Beschrijf de activiteit van het bedrijf',
            innLabel: 'Btw-nummer',
            innPlaceholder: 'Voer het btw-nummer in',
            promoCodeLabel: 'Promocode',
            promoCodePlaceholder: 'Voer promocode in',
            ifAvailable: 'indien beschikbaar',
            tariffDetailsLink: 'Meer over tarieven',
            registrationFor: 'Registratie voor',
            firstNameLabel: 'Voornaam',
            firstNamePlaceholder: 'Voer de voornaam in',
            lastNameLabel: 'Achternaam',
            lastNamePlaceholder: 'Voer de achternaam in',
            countryLabel: 'Land',
            countryPlaceholder: 'Selecteer land',
            cityLabel: 'Stad',
            cityPlaceholder: 'Voer de stad in',
            lineLabel: 'Straat en huisnummer',
            linePlaceholder: 'Voer straat en huisnummer in',
            postcodeLabel: 'Postcode',
            postcodePlaceholder: 'Voer postcode in',
            countryBelgium: 'België',
            countryFrance: 'Frankrijk',
            countryGermany: 'Duitsland',
            countryNetherlands: 'Nederland',
            countryPoland: 'Polen',
            genderLabel: 'Geslacht',
            genderMale: 'Man',
            genderFemale: 'Vrouw',
            personalAddressLabel: 'Adres',
            personalAddressPlaceholder: 'Voer het adres in',
            phoneLabel: 'Telefoon',
            phonePlaceholder: 'Voer het telefoonnummer in',
            passportFileLabel: 'Paspoortscan',
            uploadPassportFrontButton: 'Voorkant uploaden',
            uploadPassportReverseButton: 'Achterkant uploaden',
            uploadAnotherPassportFileButton: 'Ander bestand kiezen',
            passportReverseFileLabel: 'Achterkant paspoort',
            passportFrontSideLabel: 'Voorzijde van het paspoort',
            openUploadedFile: 'Bestand openen',
            completeRegistrationTitle: 'Bevestig de registratie',
            completeRegistrationMessage:
                'Na bevestiging wordt het ingevulde formulier ter moderatie verzonden.',
            completeRegistrationConfirmButton: 'Bevestigen',
            completeRegistrationCancelButton: 'Annuleren',
            completeValidationFieldsTitle: 'Controleer de volgende velden:',
            completeValidationFieldError: 'Controleer dit veld',
            completeValidationInnHint: 'Controleer het btw-/INN-veld in de stap bedrijfsgegevens',
            moderationInProgressTitle: 'Uw gegevens worden gecontroleerd',
            moderationInProgressDescription:
                'De controle duurt maximaal 24 uur. Na afronding sturen we een e-mail naar {email} met uw login en wachtwoord voor toegang tot uw persoonlijke account.',
            moderationInProgressContact: 'Als u vragen heeft, neem dan contact met ons op via:',
            returnToWebsiteButton: 'Terug naar website',
            registerButton: 'Registreren',
            requiredField: 'Verplicht veld',
            minLengthTemplate: 'Minimaal {n} tekens',
            maxLengthTemplate: 'Maximaal {n} tekens',
            phoneInvalid: 'Telefoon mag alleen cijfers, + en haakjes bevatten',
            latinOnly: 'Gebruik alleen Latijnse tekens',
        },
    },
    pages: {
        mail: {
            title: 'Post',
            forwardToHomeAddress: 'Post doorsturen naar mijn thuisadres instellen',
            forwardToHomeAddressDescription:
                'Alle brieven worden automatisch doorgestuurd naar het opgegeven adres',
            configureForwarding: 'Doorsturen configureren',
            incomingMailSettings: 'Instellingen voor postscanning',
            openQrCode: 'QR-code sleutel voor Office Hof',
            orderForwarding: 'Post doorsturen bestellen',
            forwarding: 'Doorsturen van papieren post',
            orderForwardingTitle: 'Post doorsturen bestellen',
            orderOneTimeForwarding: 'Eenmalig doorsturen bestellen',
            orderForwardingMessage:
                'Bestel het post doorsturen en we sturen al uw brieven van Office Hof naar uw adres',
            orderButton: 'Bestellen',
            orderForwardingSuccess: 'Doorsturen besteld',
            orderForwardingError: 'Fout bij bestellen doorsturen',
            forwardingSentMessage: 'Brieven zijn doorgestuurd naar uw adres. Bevestig ontvangst.',
            forwardingSentHint: 'Als brieven binnen 10 dagen niet aankomen, neem contact op met support',
            forwardingTrackNumber: 'Trackingnummer',
            forwardingTrackOnBpost: 'Volgen via bpost',
            forwardingReceivedButton: 'Doorsturen ontvangen',
            forwardingReceivedSuccess: 'Ontvangst doorsturen bevestigd',
            forwardingReceivedError: 'Fout bij bevestigen ontvangst',
            forwardingEmptyMessage:
                'Doorsturen geannuleerd omdat de brievenbus leeg was op het moment van verzoek',
            singleForwardingStatus: {
                processing: 'In behandeling',
                completed: 'Voltooid',
                pending: 'In afwachting',
                cancelled: 'Geannuleerd',
                sent: 'Verzonden',
            },
            singleForwardingRequestDate: 'Aanvraagdatum',
            singleForwardingStatusLabel: 'Status',
            cancelButton: 'Annuleren',
            qrCodeTitle: 'Persoonlijke QR-code',
            qrCodeDescription:
                'Met deze QR-code krijgt u toegang tot het kantoor, gereserveerde vergaderruimtes en uw brievenbus',
            qrCodeDescriptionWithAddress:
                'Met deze QR-code krijgt u toegang tot het kantoor op {address}, gereserveerde vergaderruimtes en uw brievenbus',
            scanContent: 'Inhoud brief scannen',
            scanContentDescription: 'Inhoud inkomende brieven automatisch scannen',
            scanningSettings: 'Scaninstellingen',
            scanningSettingsPaperStorageTooltip:
                'De papieren originelen van de brieven worden bewaard in uw persoonlijke postvak, binnen het kantoorcentrum.',
            scanningDescription:
                'Als u om privacyredenen niet wilt dat uw papieren post door onze medewerkers wordt geopend en gescand, schakelt u de onderstaande functie uit. Meldingen over binnenkomende post blijven gratis.',
            forwardingSettings: 'Doorstuurinstellingen',
            regularForwarding: 'Regelmatig doorsturen',
            oneTimeForwarding: 'Eenmalig doorsturen',
            forwardMail: 'Post doorsturen',
            forwardNever: 'Nooit',
            forwardOncePerMonth: 'Eenmaal per maand',
            forwardTwicePerMonth: 'Tweemaal per maand',
            forwardingDescription: 'Inkomende post wordt doorgestuurd naar uw thuisadres: {address}',
            changeAddressHint: 'Neem contact op met support om het adres te wijzigen',
            changeAddressButton: 'Adres wijzigen',
            forwardingInfo: 'U kunt regelmatig doorsturen instellen of eenmalig bestellen',
            sendRegisteredMail: 'Versturen als aangetekende post',
            settingsSaved: 'Instellingen opgeslagen',
            saveError: 'Fout bij opslaan instellingen',
            mailsList: 'Postlijst',
            noMails: 'Geen post',
            loading: 'Laden...',
            loadError: 'Fout bij laden post',
            unavailable: 'Niet beschikbaar',
            mailTypeLetter: 'Brief',
            mailTypeLetterNoScan: 'Brief (niet gescand)',
            mailTypeParcel: 'Pakket',
            parcelTypeSmall: 'Klein',
            letterNoScanModalTitle: 'Brief niet gescand',
            letterNoScanModalMessage:
                'Uw papieren post wordt bewaard bij Office Hof. U kunt het zelf ophalen of doorsturen bestellen.',
            letterNoScanOrderButton: 'Doorsturen bestellen',
            parcelTypeBig: 'Groot',
            viewButton: 'Bekijken',
            downloadButton: 'Downloaden',
            paymentHintButton: 'Hint',
            paymentRequiredTitle: 'Betaling vereist',
            paymentRequiredMessage: 'U moet betalen voor een nieuwe periode om deze post te bekijken',
            payButton: 'Betalen',
            searchByDate: 'Zoeken op datum',
            thisWeek: 'Deze week',
            thisMonth: 'Deze maand',
            parcelReceivedTitle: 'U heeft een {type} pakket ontvangen',
            parcelReceivedBig: 'groot',
            parcelReceivedSmall: 'klein',
            parcelPickupAddress: 'U kunt het ophalen op kantoor op het adres',
            okButton: 'OK',
            dateFrom: 'Van',
            dateTo: 'Tot',
            resetButton: 'Resetten',
            applyButton: 'Toepassen',
            dateFromAfterTo: 'Datum "Van" kan niet later zijn dan "Tot"',
            dateToBeforeFrom: 'Datum "Tot" kan niet eerder zijn dan "Van"',
            clearFilter: 'Filter wissen',
        },
        office: {
            title: 'Kantoorcentrum',
            inDevelopment: 'In ontwikkeling',
            meetingRooms: 'Vergaderruimtes',
            meetingRoomsDescription: 'Vergaderruimtes reserveren en beheren',
            coworking: 'Coworking',
            coworkingDescription: 'Coworking reserveren',
            noCoworkingAvailable: 'Geen coworkingplekken beschikbaar',
            capacity: 'Capaciteit',
            people: 'personen',
            fromPrice: 'Vanaf',
            perHour: 'per uur',
            includingVat: 'incl. btw',
            exclVat: 'excl. btw',
            book: 'Reserveren',
            location: 'Locatie',
            selectLocation: 'Selecteer locatie',
            allLocations: 'Alle locaties',
            noRoomsAvailable: 'Geen ruimtes beschikbaar',
            rooms: {
                charlemagne: {
                    name: '"Charlemagne" (Groen)',
                    description:
                        'Uitrusting: Media projector, whiteboard en markers, audiosysteem, conferentietelefoon. Extra: Koffie, thee en water gratis tijdens reservering.',
                },
                ambiorix: {
                    name: '"Ambiorix" (Rood)',
                    description:
                        'Uitgerust met LED-projector en display, whiteboard en markers, audiosysteem, conferentietelefoon. Extra: Koffie, thee en water gratis tijdens reservering.',
                },
            },
            myBookings: 'Mijn reserveringen',
            noBookings: 'U heeft nog geen reserveringen',
            bookingQrModalTitle: 'Toegang vergaderruimte',
            bookingQrModalMessage: 'Toon de QR-code om toegang te krijgen tot de vergaderruimte',
            showQrCode: 'QR-code tonen',
            hours: 'u',
            minutes: 'min',
            datePlaceholder: 'dd.mm.jjjj',
            duration: 'Duur',
            cost: 'Kosten',
            insufficientFundsTitle: 'Onvoldoende saldo',
            insufficientFundsMessage: 'U heeft onvoldoende saldo',
            insufficientFundsMessageWithAmount:
                'U heeft onvoldoende saldo voor reservering. Er ontbreekt €{amount} op uw saldo',
            showMore: 'Meer',
            hide: 'Verbergen',
            booking: 'Reservering',
            selectDate: 'Selecteer datum',
            noSlotsAvailable: 'Geen slots beschikbaar',
            roomNotSelected: 'Geen vergaderruimte geselecteerd',
            available: 'Beschikbaar',
            booked: 'Gereserveerd',
            bookingSuccess: 'Vergaderruimte succesvol gereserveerd',
            bookingError: 'Fout bij aanmaken reservering',
            startTime: 'Start',
            endTime: 'Einde',
            selectStartTime: 'Selecteer tijd',
            selectEndTime: 'Selecteer tijd',
        },
        payment: {
            title: 'Betaling',
            showContract: 'Contract met Office Hof',
            balance: 'Saldo',
            extraChargesThisMonth: 'Extra diensten deze maand in rekening gebracht',
            extraChargesSectionTitle: 'Kosten deze maand voor extra diensten',
            extraServiceTypes: {
                scan: 'Post scannen',
                parcels: 'Verzending brieven/pakketten',
                bookings: 'Vergaderruimte reserveringen',
                tariff: 'Tarief',
                postal_fees: 'Portokosten',
            },
            extraChargesTotalLabel: 'Totaal',
            tariff: 'Tarief',
            nextChargeDate: 'Volgende afschrijving: ',
            tariffNotActivated: 'Tarief wordt geactiveerd na betaling',
            replenishBalance: 'Saldo aanvullen',
            replenish: 'Aanvullen',
            pay: 'Betalen',
            paySubscriptionTitle: 'Betaal het abonnement',
            paySubscriptionMessage: 'Deze pagina is beschikbaar na betaling van het abonnement',
            availableAfterPrepayment: 'Beschikbaar na vooruitbetaling',
            enterAmount: 'Voer bedrag in en selecteer betaalmethode',
            paymentCredited: 'Betalingen worden meestal direct bijgeschreven, soms kan het tot 2 dagen duren',
            paymentScreenTitle: 'Betaling',
            enterAmountForPayment: 'Voer te betalen bedrag in',
            amountForPayment: 'Te betalen bedrag',
            replenishBalanceOn: 'Saldo aanvullen met:',
            totalToPay: 'Totaal te betalen:',
            payByRequisites: 'Betalen per overschrijving',
            requisiteCompany: 'Bedrijf',
            payOnline: 'Online betalen',
            payFor1Month: 'Betalen voor 1 maand',
            payFor3Months: 'Betalen voor 3 maanden',
            payFor6Months: 'Betalen voor 6 maanden',
            payFor12Months: 'Betalen voor 1 jaar',
            discount: 'Korting',
            discountOnTariff: 'Korting {discount}% op het tarief voor {months} maanden!',
            discountOnTariffTooltip:
                'Bij betaling van €{amount} krijgt u {discount}% korting op het tarief voor {months} maanden!',
            customAmount: 'Eigen bedrag',
            minAmountError: 'Minimum betaalbedrag: {amount}€',
            serverError: 'Serverfout, we zijn ermee bezig',
            chargesHistory: 'Afschrijvingsgeschiedenis',
            invoice: 'Factuur van',
            noInvoices: 'Geen facturen gevonden',
            noTariff: 'U heeft geen tarief',
            currentTariff: 'Huidig',
            sameAsPreviousTariff: 'Alles van {tariffName}, en ook:',
            fromPrice: 'Vanaf',
            perMonth: 'per maand',
            withoutVat: 'excl. btw 21%',
            goToTariff: 'Tarief wijzigen',
            tariffUpgradeSubject: 'Verzoek van klant {customer_id} om tarief te upgraden naar {tariff_name}',
            tariffUpgradeBody: 'Verzoek om mijn tarief te verhogen naar {tariff_name}',
            tariffFeatures: {
                legalAddress: 'Het juridisch adres van uw bedrijf',
                receptionMails: 'Ontvangst van uw inkomende post (aangetekend incl.) en pakketten',
                coffeeSoftDrinks: 'Koffie en frisdrank voor u en uw klanten in de vergaderruimte',
                access24_7: 'Toegang tot uw gegevens en berichten 24/7',
                sharedFaxLine: 'Gedeelde faxlijn (behandeld als gewone post)',
                mailScanning: 'Uw inkomende post scannen en doorsturen naar uw mailbox dagelijks',
                freePages: 'Aantal gratis vellen (scannen en doorsturen A4 dubbelzijdig) in maandabonnement',
                personalBox: 'Persoonlijke kluis voor documenten, toegang 24/7',
                ipTelephony: 'IP-telefonie inclusief toestel in Office Hof IP PBX',
                freeMeetingHours: 'Aantal gratis reserveringsuren vergaderruimtes per maand',
                freeParcels: 'Aantal gratis inkomende pakketten in maandabonnement',
                discountLevel:
                    'Kortingsniveau bij reservering vergaderruimte en werkplek, post scannen en pakketten',
                brusselsPhoneLine: "Brusselse directe telefoonlijn (ook andere regio's/landen) via IP PBX",
                phoneCallsManagement: 'Beheer inkomende en uitgaande telefoon en fax met dedicated IP PBX',
            },
        },
        support: {
            title: 'Neem contact op met support',
            phone: 'Telefoon',
            email: 'E-mail',
            phoneHint: '9–18 uur, weekdagen',
            writeButton: 'Schrijf naar support',
            emailSubject: 'Verzoek van klant {customer_id}',
            editDataEmailSubject: 'Gegevenswijzigingsverzoek van klant {customer_id}',
            innEmailSubject: 'Informatie van klant {customer_id}',
            innEmailBody: 'Hallo!\n\nMijn btw-nummer: \n',
            innModalTitle: 'Btw-nummer vereist',
            innModalMessage: 'Geef uw btw-nummer op — wij sturen het ter controle door.',
            innModalSubmitButton: 'Verzenden',
            innModalLaterButton: 'Later',
            innModalCloseButton: 'Sluiten',
            innRequiredModalTitle: 'Fiscaal nummer vereist',
            innRequiredModalMessage:
                'De termijn van {n} dagen om uw fiscaal nummer door te geven is verstreken. Vul het hieronder in om de service te blijven gebruiken.',
            innInputLabel: 'Btw-nummer',
            innInputPlaceholder: 'Voer uw btw-nummer in',
            innInputRequired: 'Voer uw btw-nummer in',
            innResubmitButton: 'Opnieuw verzenden',
            innPendingTitle: 'Btw-nummer wordt gecontroleerd',
            innPendingMessage:
                'We hebben uw btw-nummer {tax_id} ontvangen en ter controle doorgestuurd. Tot de controle is afgerond blijft de toegang tot de service beperkt.',
            innPendingResubmitHint:
                'De controle duurt langer dan gebruikelijk. U kunt het btw-nummer opnieuw verzenden.',
            innSubmitError: 'Het btw-nummer kon niet worden verzonden. Probeer het later opnieuw.',
            innButton: 'Btw-nummer doorgeven',
            loading: 'Laden...',
            copied: 'Gekopieerd naar klembord',
        },
        recovery: {
            title: 'Herstel',
            description: 'Om de toegang tot het account {email} te herstellen, klik op de knop hieronder.',
            descriptionWithCompany:
                'Om de toegang tot het account {email} van het bedrijf {company_name} te herstellen, klik op de knop hieronder.',
            button: 'Herstellen',
            successMessage: 'We hebben u een e-mail gestuurd met een herstellink.',
            successHint: 'Als de e-mail niet is aangekomen, probeer het later opnieuw.',
            invalidEmail: 'Ongeldig e-mailadres',
        },
        profile: {
            title: 'Profiel',
            companyData: 'Bedrijfsgegevens',
            contactPerson: 'Contactpersoon',
            mailingAddress: 'Postadres',
            settings: 'Instellingen',
            companyName: 'Naam',
            inn: 'Btw-nummer',
            officeAddress: 'Kantooradres',
            name: 'Naam',
            email: 'E-mail',
            phone: 'Telefoon',
            interfaceLanguage: 'Taal van de interface',
            editData: 'Gegevens bewerken',
            editDataMessage: 'Neem contact op met support om uw of bedrijfsgegevens te wijzigen',
            logoutButton: 'Uitloggen',
            loading: 'Profielgegevens laden...',
            languageNames: {
                ru: 'Russisch',
                en: 'Engels',
                fr: 'Frans',
                nl: 'Nederlands',
                de: 'Duits',
            },
        },
    },
    installPrompt: {
        title: 'App installeren',
        description: 'Installeer de app voor snellere en eenvoudigere toegang tot Office Hof',
        install: 'Installeren',
        skip: 'Overslaan',
        androidTitle: 'Installeren op Android',
        androidSteps: [
            'Open het browsermenu (drie puntjes rechtsboven)',
            'Selecteer «Toevoegen aan startscherm» of «App installeren»',
            'Bevestig installatie',
        ],
        iosTitle: 'Installeren op iPhone',
        iosSteps: [
            'Tik op de deelknop (vierkant met pijl omhoog) in Safari',
            'Scroll en selecteer «Zet op beginscherm»',
            'Tik op «Voeg toe»',
        ],
        close: 'Sluiten',
    },
};
